import Head from 'next/head';
import getConfig from 'next/config';
import { NextSeo } from 'next-seo';
import type { GetServerSidePropsContext } from 'next';

import { VerificationProvider } from 'contexts/VerificationContext';
import { DefaultLayout } from 'components/Layouts';
import {
  Notifications,
  ResponseMakeModelType,
  ResponseModelFilter,
} from 'types';
import { formatServerError } from 'helpers/logger';
import { PAGE } from 'helpers/pages';
import { getTLD } from 'helpers/domain';
import { showroomRepository } from 'repositories/ShowroomRepository';
import { Dealer } from 'domains/Dealer';
import { isOk } from 'domains/Result';
import DealerPage, { DealerPages } from 'components/DealerPage/DealerPage';
import {
  IDFP,
  IGetAdsRequestFilter,
  IGetAdsRequestRange,
  IGetSection,
  ISearchPageAds,
  ISearchPageFilter,
  ISearchPagePaging,
  ISearchPageSeoData,
} from 'api/types/searchPageApiTypes';
import { GTMEvent } from 'services/gtmService';
import { DealerPageProvider } from 'components/DealerPage/context/DealerPageContext';
import type { ViewType } from 'components/DealerPage/DealerPage.typed';
import {
  createHiddenFilters,
  mapRangesRequest,
} from 'components/DealerPage/mappers/filters';
import {
  generateGetAdsRequestParams,
  getSectionParams,
} from 'helpers/seo/searchParams';
import {
  getMakeModelFilters,
  mapLegacyQueryParams,
} from 'pages/search/[...section]';
import { searchPageApi } from 'api/searchPageApi';
import { ListItem } from 'components/Toolkit/Inputs/CustomSelect';
import { Coordinates } from 'features/location/Location.typed';
import { DealerFilterProvider } from 'components/DealerPage/context/DealerFilterContext';
import { getSeoTitle, getSeoDescription } from 'helpers/seo/dealerShowroomSeo';

const {
  publicRuntimeConfig: { BASE_URL, CDN_STATIC_ASSETS, API_LISTINGS_TIMEOUT },
} = getConfig();

interface DealerPageProps {
  type: 'success';
  domain: string;
  notification: Notifications;
  dealer: Dealer;
  ads: ISearchPageAds[];
  sort: string;
  section: string;
  sectionsData: IGetSection[];
  sectionName: string;
  parentName: string | null;
  parentDisplayName: string | null;
  searchMatches: number;
  paging: ISearchPagePaging;
  viewType: ViewType;
  saved: boolean;
  userId?: string | string[];
  gtm: GTMEvent;
  dfp: IDFP;
  seoData: ISearchPageSeoData;
  filtersData: ISearchPageFilter[];
  filtersMakeModel: ResponseModelFilter;
  makeModelParams: ResponseMakeModelType;
  filtersQueryParams: IGetAdsRequestFilter[];
  rangeQueryParams: IGetAdsRequestRange[];
  selectedCounty?: ListItem | null;
  radius: string | null;
  locationDisplay: string | null;
  coordinates: Coordinates;
  countyTown: string | null;
  countyList: ListItem[];
  radiusList: {
    label: string;
    value: string;
  }[];
  page: DealerPages;
  pageParam: string;
  seoDisplayName: string;
  seoCategoryDisplayName: string;
}

function Section({
  domain,
  notification,
  dealer,
  ads,
  section,
  sectionsData,
  searchMatches,
  parentDisplayName,
  sectionName,
  parentName,
  sort,
  paging,
  saved,
  viewType,
  userId,
  gtm,
  dfp,
  seoData,
  filtersData,
  filtersMakeModel,
  makeModelParams,
  filtersQueryParams,
  rangeQueryParams,
  page,
  pageParam,
  seoDisplayName,
  seoCategoryDisplayName,
}: DealerPageProps) {
  const { name, address, slug } = dealer;

  const hasAds = ads && ads.length > 0;

  const getSeoCanonicalUrl = () => {
    const formattedPageParam = pageParam ? `/${pageParam}` : '';
    return `${domain}/dealer/${slug}${formattedPageParam}`;
  };

  return (
    <VerificationProvider>
      <NextSeo
        title={getSeoTitle({
          page,
          name,
          seoDisplayName,
          address,
        })}
        noindex={!hasAds}
        nofollow={!hasAds}
        description={getSeoDescription({
          page,
          name,
          seoCategoryDisplayName,
          address,
        })}
        canonical={getSeoCanonicalUrl()}
      />
      <Head>
        <meta
          name="google-site-verification"
          content="GHUykIchXG6tmbMfI3F6rSG5MVDBjzJN6iCyRilt8Kw"
        />
      </Head>

      <DefaultLayout
        domain={domain}
        headerVariant="TRANSPARENT"
        notificationData={notification}
      >
        <DealerPageProvider
          sort={sort}
          section={section}
          paging={paging}
          viewType={viewType}
          saved={saved}
          userId={userId}
          baseUrl={domain}
          keywordSearchTerm={''}
          initialBreadcrumbs={[]}
          initialRecommendations={[]}
        >
          <DealerFilterProvider
            filtersData={filtersData}
            filtersMakeModelData={filtersMakeModel}
            filtersQueryParams={filtersQueryParams}
            makeModelParams={makeModelParams}
            rangeQueryParams={rangeQueryParams}
          >
            <DealerPage
              filtersData={filtersData}
              cdnUrl={CDN_STATIC_ASSETS}
              domain={domain}
              dealer={dealer}
              ads={ads}
              gtm={gtm}
              dfp={dfp}
              seoData={seoData}
              sectionsData={sectionsData}
              searchMatches={searchMatches}
              parentDisplayName={parentDisplayName}
              sectionName={sectionName}
              parentName={parentName}
              page={page}
            />
          </DealerFilterProvider>
        </DealerPageProvider>
      </DefaultLayout>
    </VerificationProvider>
  );
}

const getPageName = (section: string) => {
  switch (section) {
    case 'contact':
      return DealerPages.contact;
    case 'reviews':
      return DealerPages.reviews;
    default:
      return DealerPages.our_stock;
  }
};

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext<{ section: string[] }>,
) => {
  const tld = getTLD(ctx.req);
  let domain = `${BASE_URL}${tld}`;
  try {
    /**
     * If no dealerId is found we set the statusCode to 404 and respond.
     * We also do not catch any errors here as we want to allow Next to handle.
     */
    const dealerSlug = ctx.params?.section[0];

    if (!dealerSlug) {
      return { notFound: true };
    }

    const filterData = [...createHiddenFilters()];
    delete ctx.params?.section[0];

    const queryParams = mapLegacyQueryParams(ctx.query);
    const pageParam = ctx.params?.section[1] ?? '';
    let section = pageParam || 'all';
    const pageName = getPageName(section);
    let seoDisplayName = 'Car Dealer';
    let seoCategoryDisplayName = 'Car';

    if (pageName === DealerPages.reviews || pageName === DealerPages.contact) {
      section = 'all';
    }

    const {
      isMakeModel,
      sectionQueryParams,
      makeModelFilter,
      makeModelParams,
    } = getSectionParams(filterData, queryParams);

    const getAdsRequestParams = generateGetAdsRequestParams(
      section,
      {
        ...queryParams,
        ...sectionQueryParams,
      },
      filterData,
      '',
      10,
    );

    // Only needed for initial API request.
    // DO NOT pass this to the client.
    const mappedGetAdsRequestParams = {
      ...getAdsRequestParams,
      ...(isMakeModel && {
        makeModelFilters: makeModelParams,
      }),
      ranges: getAdsRequestParams.ranges
        ? mapRangesRequest(getAdsRequestParams.ranges)
        : [],
    };

    const cookie = ctx.req.headers.cookie;

    const dealerShowroomResponse = await showroomRepository.getDealerShowroom(
      dealerSlug,
      mappedGetAdsRequestParams,
      {
        ...(cookie ? { cookie } : {}),
      },
    );

    const keywordSearchTerms = queryParams.words
      ? Array.isArray(queryParams.words)
        ? queryParams.words[0]
        : queryParams.words
      : null;

    if (isOk(dealerShowroomResponse)) {
      const dealerId = dealerShowroomResponse.ok.dealer.id;
      const dealerIdFilter = {
        name: 'dealerId',
        values: [dealerId],
      };

      const sections = await searchPageApi.getSections(
        section,
        {
          ...mappedGetAdsRequestParams,
          filters: mappedGetAdsRequestParams.filters.concat(dealerIdFilter),
          terms: keywordSearchTerms,
        },
        API_LISTINGS_TIMEOUT,
      );

      const filters = await searchPageApi.getFiltersDealerShowroom(
        section,
        { accept: 'application/json' },
        dealerId,
      );
      const filterData = [
        ...filters.data.showByDefault,
        ...createHiddenFilters(),
      ];

      const makeModelFiltersData = await getMakeModelFilters(
        section,
        makeModelFilter,
        makeModelParams,
        dealerId,
      );

      const dealerShowroomSeoResponse =
        await showroomRepository.getDealerShowroomSeo(dealerId);

      if (isOk(dealerShowroomSeoResponse)) {
        seoDisplayName = dealerShowroomSeoResponse.ok.seoDisplayName;
        seoCategoryDisplayName =
          dealerShowroomSeoResponse.ok.seoCategoryDisplayName;
      }

      const hasReviews = Boolean(dealerShowroomResponse.ok.dealer.googleReview);

      if (pageName === DealerPages.reviews && !hasReviews) {
        return {
          redirect: {
            permanent: true,
            destination: `/dealer/${dealerShowroomResponse.ok.dealer.slug}`,
          },
        };
      }

      return {
        props: {
          sort: getAdsRequestParams.sort || '',
          section,
          filtersData: filterData || [],
          filtersMakeModel: makeModelFiltersData || [],
          makeModelParams: [],
          filtersQueryParams: [],
          rangeQueryParams: [],
          sectionsData: sections.data.subSections || [],
          sectionName: sections.data.displayName,
          parentName: sections.data.parentName || null,
          parentDisplayName: sections.data.parentDisplayName || null,
          searchMatches: sections.data.searchMatches || 0,
          paging: dealerShowroomResponse.ok.paging,
          gtm: {},
          dfp: {
            vertical: [],
            section: [],
            words: [],
            area: [],
            user_id: [],
          },
          seoData: {
            internalLinks: [],
          },
          viewType: 'list',
          saved: false,
          userId: '',
          dealer: dealerShowroomResponse.ok.dealer,
          notification: dealerShowroomResponse.ok.notification,
          ads: dealerShowroomResponse.ok.ads,
          domain,
          type: 'success',

          selectedCounty: null,
          radius: [],
          locationDisplay: '',
          coordinates: [],
          countyTown: null,
          countyList: [],
          radiusList: [],
          page: pageName,
          pageParam,
          seoDisplayName,
          seoCategoryDisplayName,
        },
      };
    } else {
      console.error(
        formatServerError({
          route: PAGE.DEALER_DETAILS,
          error: dealerShowroomResponse.error,
          message: 'Failed to get dealer data',
        }),
      );
      return { notFound: true };
    }
  } catch (error) {
    console.error(
      formatServerError({
        route: PAGE.DEALER_DETAILS,
        error,
        message: 'Failed to get dealer data',
      }),
    );
    return { notFound: true };
  }
};

export default Section;
